// Progress/counting helpers for inspections.
// Two inspection formats coexist in InspectScreen:
//   - options   (fire / moveout): flat `items`, each answered with one option string
//   - checklist  (onboarding):    `sections` of `steps`, each step marked Done or Skip/N-A
// These helpers give both formats a single { done, total } shape so the form,
// list, and "new" views don't each re-implement the branching.

export const isChecklist = (def) => def?.format === 'checklist';

export const checklistSteps = (def) => (def?.sections || []).flatMap((s) => s.steps || []);

// A checklist step is "addressed" once the tech marks it done or skipped.
export const stepAddressed = (entry) => entry?.status === 'done' || entry?.status === 'skipped';

// { done, total } for either format, given the def and its saved items/formData map.
export function inspectionProgress(def, data = {}) {
  if (isChecklist(def)) {
    const steps = checklistSteps(def);
    return { done: steps.filter((s) => stepAddressed(data[s.id])).length, total: steps.length };
  }
  const items = def?.items || [];
  return { done: items.filter((it) => data[it.id]).length, total: items.length };
}

export const inspectionItemCount = (def) =>
  isChecklist(def) ? checklistSteps(def).length : def?.items?.length || 0;

// Ordered review slides for a checklist inspection: one per step that carries a
// note and/or photos, in section→step order. Each slide keeps the section/step
// titles so the notes modal can page through them with context.
export function stepNotes(def, data = {}) {
  if (!isChecklist(def)) return [];
  return (def.sections || [])
    .flatMap((sec) =>
      (sec.steps || []).map((step) => {
        const entry = data[step.id] || {};
        return {
          id: step.id,
          sectionTitle: sec.title,
          title: step.title,
          note: entry.note || '',
          photos: entry.photos || [],
        };
      })
    )
    .filter((s) => s.note.trim() || s.photos.length);
}
